/**
 * RevenueCat wrapper for Slimmy (Capacitor edition).
 *
 * Replaces the previous Natively `NativelyPurchases` bridge. Uses the
 * official `@revenuecat/purchases-capacitor` plugin on iOS/Android.
 * On the web (PWA) it falls back to "no-op + descriptive error" so the
 * Premium screen can route to the Stripe checkout instead.
 *
 * Public surface kept compatible with the old file so Premium.js
 * doesn't need to change: rcLogin, rcPurchase, rcRestore,
 * isRevenueCatAvailable, syncWithBackend.
 */
import axios from 'axios';
import { API_URL } from '../config/api';
import { httpClient } from './http';

let _initialised = false;
let _purchasesModuleCache = null;

/** Lazy import of the Capacitor plugin so the Web bundle doesn't fail. */
async function getPurchasesModule() {
  if (_purchasesModuleCache !== null) return _purchasesModuleCache;
  try {
    const { Capacitor } = await import('@capacitor/core');
    const plat = Capacitor.getPlatform();
    if (plat !== 'ios' && plat !== 'android') {
      _purchasesModuleCache = false;
      return false;
    }
    const mod = await import('@revenuecat/purchases-capacitor');
    _purchasesModuleCache = mod;
    return mod;
  } catch (e) {
    console.warn('[RevenueCat] plugin unavailable:', e?.message || e);
    _purchasesModuleCache = false;
    return false;
  }
}

export function isRevenueCatAvailable() {
  if (typeof window === 'undefined') return false;
  const plat = window.Capacitor?.getPlatform?.();
  return plat === 'ios' || plat === 'android';
}

/** Configure the SDK once per session with the right API key for the
 *  platform. Reads the public keys from `/api/payments/config` so we
 *  don't have to bundle them at build-time. Safe to call repeatedly. */
async function ensureConfigured() {
  if (_initialised) return true;
  const mod = await getPurchasesModule();
  if (!mod) return false;
  const { Purchases, LOG_LEVEL } = mod;

  // Pull the per-platform public key from the backend (already exposed
  // via the admin Payments tab) so rotating keys doesn't require an
  // app re-deploy.
  let publicKey = null;
  try {
    const { Capacitor } = await import('@capacitor/core');
    const platform = Capacitor.getPlatform();
    const { data: cfg } = await httpClient.get(`${API_URL}/api/payments/config`);
    const rc = cfg?.revenuecat || {};
    publicKey = platform === 'ios' ? rc.public_key_ios : rc.public_key_android;
  } catch (e) {
    console.warn('[RevenueCat] could not fetch config:', e);
  }
  if (!publicKey) {
    throw new Error('RevenueCat public key non configurata in Admin → Payments');
  }

  try {
    if (typeof Purchases.setLogLevel === 'function') {
      await Purchases.setLogLevel({ level: LOG_LEVEL?.WARN || 'WARN' });
    }
    await Purchases.configure({ apiKey: publicKey });
    _initialised = true;
    return true;
  } catch (e) {
    console.error('[RevenueCat] configure failed:', e);
    throw new Error('Errore inizializzazione RevenueCat: ' + (e?.message || e));
  }
}

/**
 * Bind the current Slimmy user to a RevenueCat customer.
 * Idempotent — safe to call on every Premium page load.
 */
export async function rcLogin(userId, userEmail = '') {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) throw new Error('RevenueCat non disponibile fuori dall\'app nativa');
  const { Purchases } = mod;
  try {
    const res = await Purchases.logIn({ appUserID: userId });
    if (userEmail) {
      try { await Purchases.setEmail({ email: userEmail }); } catch { /* */ }
    }
    return res;
  } catch (e) {
    console.error('[RevenueCat] logIn error:', e);
    throw new Error('Login RevenueCat fallito: ' + (e?.message || e));
  }
}

/** Returns the current Offerings — useful to display localised price strings. */
export async function rcGetOfferings({ forceRefresh = false } = {}) {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) return null;
  const { Purchases } = mod;
  if (forceRefresh) {
    try { if (typeof Purchases.invalidateCustomerInfoCache === 'function') await Purchases.invalidateCustomerInfoCache(); } catch (_) {}
    try { if (typeof Purchases.syncPurchases === 'function') await Purchases.syncPurchases(); } catch (_) {}
  }
  try {
    const offerings = await Purchases.getOfferings();
    return offerings?.current || null;
  } catch (e) {
    console.error('[RevenueCat] getOfferings error:', e);
    return null;
  }
}

/**
 * Direct StoreKit query for product details. Unlike `getOfferings()`
 * (which can return the RevenueCat dashboard's fallback storefront
 * prices for anonymous users), `getProducts` interrogates StoreKit on
 * the device with the actual Apple ID storefront — so an Italian
 * Apple ID always returns EUR even when the RC dashboard default is
 * configured on US. Use this as the authoritative source for the
 * prices displayed in the Premium card.
 */
export async function rcGetProducts(productIdentifiers) {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) return [];
  const { Purchases } = mod;
  try {
    const res = await Purchases.getProducts({ productIdentifiers });
    return res?.products || res || [];
  } catch (e) {
    console.warn('[RevenueCat] getProducts failed:', e);
    return [];
  }
}

/**
 * Trigger the native StoreKit / Play Billing purchase flow for the
 * given package identifier (e.g. `$rc_monthly`, `$rc_annual` or the
 * custom identifier configured in your RevenueCat offering).
 *
 * Throws on cancel/failure. Returns the customer info on success.
 */
export async function rcPurchase(packageIdentifier) {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) throw new Error('Acquisti non disponibili fuori dall\'app');
  const { Purchases } = mod;

  // Resolve the Package object first — the plugin needs the full
  // object, not just the identifier string.
  const current = await rcGetOfferings();
  if (!current) throw new Error('Nessuna offerta RevenueCat trovata');
  const pkg = (current.availablePackages || []).find(
    (p) => p.identifier === packageIdentifier
      || p.product?.identifier === packageIdentifier,
  );
  if (!pkg) {
    throw new Error(`Pacchetto "${packageIdentifier}" non trovato nell'offerta corrente`);
  }

  try {
    console.log('[RevenueCat] purchasePackage BEFORE', {
      packageIdentifier,
      product_identifier: pkg.product?.identifier,
      product_price: pkg.product?.price,
      product_priceString: pkg.product?.priceString,
      product_currencyCode: pkg.product?.currencyCode,
      offeringIdentifier: pkg.offeringIdentifier,
    });
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    console.log('[RevenueCat] purchasePackage AFTER', {
      transactionId: result?.transaction?.transactionIdentifier
        || result?.customerInfo?.originalAppUserId,
      productIdentifier: result?.productIdentifier,
      entitlements_active: Object.keys(result?.customerInfo?.entitlements?.active || {}),
    });
    return {
      customerInfo: result?.customerInfo,
      productIdentifier: result?.productIdentifier,
      transactionId: result?.customerInfo?.originalAppUserId,
    };
  } catch (e) {
    // The plugin throws with userCancelled=true when the user backed out
    if (e?.userCancelled || /cancel/i.test(e?.message || '')) {
      const err = new Error('Acquisto annullato');
      err.code = 'CANCELLED';
      throw err;
    }
    console.error('[RevenueCat] purchase error:', e);
    throw new Error(e?.message || 'Acquisto non completato');
  }
}

/** "Restore Purchases" — REQUIRED by Apple App Store guidelines. */
export async function rcRestore() {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) throw new Error('Ripristino non disponibile fuori dall\'app');
  const { Purchases } = mod;
  try {
    const result = await Purchases.restorePurchases();
    return result;
  } catch (e) {
    console.error('[RevenueCat] restore error:', e);
    throw new Error(e?.message || 'Nessun acquisto da ripristinare');
  }
}

/** Returns true if the current user owns the "premium" entitlement. */
export async function rcIsPremium(entitlementId = 'premium') {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) return false;
  const { Purchases } = mod;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return !!customerInfo?.entitlements?.active?.[entitlementId];
  } catch {
    return false;
  }
}

/** Returns the current RevenueCat appUserID (anon or logged-in). */
export async function rcAppUserID() {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) return null;
  const { Purchases } = mod;
  try {
    const res = await Purchases.getAppUserID();
    return res?.appUserID || res || null;
  } catch (e) {
    console.warn('[RevenueCat] getAppUserID failed:', e);
    return null;
  }
}

/** Returns the full customer info object (entitlements, originalAppUserId, …). */
export async function rcCustomerInfo() {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) return null;
  const { Purchases } = mod;
  try {
    const res = await Purchases.getCustomerInfo();
    return res?.customerInfo || res || null;
  } catch (e) {
    console.warn('[RevenueCat] getCustomerInfo failed:', e);
    return null;
  }
}

/**
 * Hard-reset of the RevenueCat session on this device. Used by the
 * Diagnostica → "Reset RevenueCat session" button when offerings come
 * back with the wrong storefront / currency cached from a previous
 * sandbox tester. Sequence:
 *   1. logOut() — drops the current appUserID, reverts to anonymous
 *   2. invalidateCustomerInfoCache() — flushes the CustomerInfo cache
 *   3. syncPurchases() — re-syncs receipts with the App Store
 */
export async function rcResetSession() {
  await ensureConfigured();
  const mod = await getPurchasesModule();
  if (!mod) throw new Error('RevenueCat non disponibile');
  const { Purchases } = mod;
  const log = [];
  const safe = async (label, fn) => {
    try { const r = await fn(); log.push({ step: label, ok: true, result: r ?? 'ok' }); }
    catch (e) { log.push({ step: label, ok: false, error: e?.message || String(e) }); }
  };
  await safe('logOut',                       () => Purchases.logOut?.());
  await safe('invalidateCustomerInfoCache',  () => Purchases.invalidateCustomerInfoCache?.());
  await safe('syncPurchases',                () => Purchases.syncPurchases?.());
  return log;
}

/**
 * After a successful purchase / restore, ask the backend to refresh
 * its view of the entitlement by talking to RevenueCat directly. The
 * backend is the source of truth for `is_premium`.
 */
export async function syncWithBackend(customerId, packageId, transactionId) {
  return httpClient.post(`${API_URL}/api/payments/revenuecat/sync`, {
    customer_id: customerId,
    package_id: packageId || null,
    transaction_id: transactionId || null,
  });
}

// Re-export plain axios helper for callers that prefer it.
export const API = axios;
